import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import { toastPermissionDenied } from '../utils/permissionToast';
import { useAuth } from '../contexts/AuthContext';
import AppraisalForm from '../components/AppraisalForm';
import { canEditAppraisal, canEditAppraisals } from '../utils/compensationPermissions';

const EditAppraisal = () => {
  const { hasPermission, user, loading } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  
  const methods = useForm();
  const { handleSubmit, reset, formState: { isSubmitting } } = methods;
  
  const [employees, setEmployees] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [appraisalLoaded, setAppraisalLoaded] = useState(false);
  const [fetchedAppraisal, setFetchedAppraisal] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  // Full edit (ratings, manager comments, the raise) is for HR/admin (update_all) and
  // department managers (update_reports) only. appraisals:update_own is deliberately NOT
  // enough: every employee holds it, solely for the self-review on AppraisalDetail. Once
  // the record is loaded, the server's own-record rule applies too, so a manager is
  // turned away from their own appraisal here rather than by a 403 on save.
  const canEdit = fetchedAppraisal
    ? canEditAppraisal(hasPermission, user, fetchedAppraisal)
    : canEditAppraisals(hasPermission);

  useEffect(() => {
    // Wait for AuthContext to resolve - during initial load/refresh the permission
    // helpers return false transiently, which would prematurely redirect an authorized user.
    if (loading) return;
    if (!canEdit) {
      toastPermissionDenied('You do not have permission to edit appraisals');
      navigate('/appraisals');
    }
  }, [loading, canEdit, navigate]);

  useEffect(() => {
    // Only the latest run of this effect may populate the form. StrictMode runs it
    // twice in dev and an id change re-runs it; without this guard the stale
    // response lands after the user has started typing, and the reset effect below
    // replaces their edits with the fetched values, which is what the PUT then sends.
    let cancelled = false;
    const fetchData = async () => {
      try {
        const [appraisalRes, empRes, pmRes] = await Promise.all([
          api.get(`/api/appraisals/${id}`),
          api.get('/api/employees', { params: { limit: 10000 } }), // Fetch all employees for dropdown
          api.get('/api/payment-methods')
        ]);
        if (cancelled) return;
        const appraisalData = appraisalRes.data.data;
        // /api/payment-methods lists active methods only. A method deactivated since this
        // appraisal was saved would show as "Select method" yet pass the required rule and
        // be sent again, so treat it as empty and make HR pick an active one. Safe for the
        // salary: an unchanged raise amount and currency move it by nothing.
        const activeMethods = pmRes.data.data || [];
        if (!activeMethods.some(pm => String(pm.id) === String(appraisalData.payment_method_id))) {
          appraisalData.payment_method_id = null;
        }
        setFetchedAppraisal(appraisalData);
        setEmployees(empRes.data.data || []);
        setPaymentMethods(activeMethods);
        setAppraisalLoaded(true);
      } catch (err) {
        if (cancelled) return;
        toast.error('Failed to load data');
        setLoadFailed(true);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (appraisalLoaded && fetchedAppraisal) {
      reset(fetchedAppraisal);
    }
  }, [appraisalLoaded, fetchedAppraisal, reset]);

  const onSubmit = async (data) => {
    try {
      await api.put(`/api/appraisals/${id}`, data);
      toast.success('Appraisal updated!');
      navigate('/appraisals');
    } catch (err) {
      // express-validator 400s carry the reason in errors[], not message.
      toast.error(
        err.response?.data?.message
          || err.response?.data?.errors?.[0]?.msg
          || 'Failed to update appraisal'
      );
    }
  };

  if (!appraisalLoaded) {
    return (
      <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-soft">
        <div className="text-center">{loadFailed ? 'Could not load this appraisal.' : 'Loading...'}</div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-soft">
      <h2 className="text-2xl font-bold mb-6">Edit Appraisal</h2>
      <FormProvider {...methods}>
        {/* noValidate: react-hook-form owns validation. Without it the raise amount's
            step="0.01" puts up the browser's own popup instead of the field message. */}
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          <AppraisalForm 
            employees={employees}
            paymentMethods={paymentMethods}
            mode="edit"
          />
          <button type="submit" disabled={isSubmitting} data-testid="appraisals-submit" className="w-full bg-primary-600 text-white py-2 rounded-md hover:bg-primary-700 transition-colors">
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </FormProvider>
    </div>
  );
};

export default EditAppraisal; 